import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { FieldsGrid, SettingsActions, SettingsCard, SettingsField, SettingsTable, SettingsToggle } from "./SettingsPrimitives";
import { settingsRequest } from "../../utils/settingsApi";

const notificationTypes = [
  ["assignments_mentions", "Assignments and mentions"],
  ["candidate_updates", "Candidate / recruiting updates"],
  ["sales_follow_ups", "Sales / CRM follow-ups"],
  ["conversations", "Conversations"],
  ["calendar_changes", "Calendar / interview changes"],
  ["agent_review_required", "Agent review required", true],
  ["connector_critical_failure", "Connector critical failure", true],
  ["account_security_event", "Account / security event", true],
  ["permission_change", "Permission change affecting the user", true],
  ["user_action_failure", "Failure of a user-initiated action", true],
];

const notificationDefaults = Object.fromEntries(notificationTypes.map(([key]) => [
  key, { in_app: true, email: true },
]));
const notificationSettingsDefaults = { preferences: notificationDefaults };

const workflowDefaults = {
  candidate_duplicate_detection: true,
  candidate_status_automation: true,
  candidate_job_stage_automation: true,
  public_candidate_links: true,
  candidate_follow_up_sla: "1 business day",
  presented_candidate_reminder: "2 business days",
  default_lead_owner: "Record Creator",
  do_not_contact_enforcement: true,
  sales_status_automation: true,
  lead_follow_up_sla: "1 business day",
  discovery_follow_up_reminder: "2 business days",
  proposal_follow_up_reminder: "3 business days",
  stalled_lead_threshold: "7 business days",
  public_job_posting_links: true,
  proposal_template: "Standard Recruitment Proposal",
  fee_agreement_template: "Standard Placement Agreement",
  job_posting_template: "Standard Job Advertisement",
  organization_email_signature: "",
  default_sequence_sender: "Sequence Owner",
  team_public_profile_sharing: true,
  client_portal_default_job_access: "Selected Jobs",
};

const workflowGroups = [
  [
    ["Candidate Duplicate Detection", "candidate_duplicate_detection", "toggle"],
    ["Candidate Status Automation", "candidate_status_automation", "toggle"],
    ["Candidate-Job Stage Automation", "candidate_job_stage_automation", "toggle"],
    ["Public Candidate Links", "public_candidate_links", "toggle"],
    ["Candidate Follow-up SLA", "candidate_follow_up_sla", "sla"],
    ["Presented Candidate Reminder", "presented_candidate_reminder", "presented"],
  ],
  [
    ["Default Lead Owner", "default_lead_owner", ["Record Creator", "Assigned Owner"]],
    ["Do Not Contact Enforcement", "do_not_contact_enforcement", "toggle"],
    ["Sales Status Automation", "sales_status_automation", "toggle"],
    ["Lead Follow-up SLA", "lead_follow_up_sla", "sla"],
    ["Discovery Follow-up Reminder", "discovery_follow_up_reminder", "sla"],
    ["Proposal Follow-up Reminder", "proposal_follow_up_reminder", "sla"],
    ["Stalled Lead Threshold", "stalled_lead_threshold", "sla"],
  ],
  [
    ["Public Job Posting Links", "public_job_posting_links", "toggle"],
    ["Proposal Template", "proposal_template", "template"],
    ["Fee Agreement Template", "fee_agreement_template", "template"],
    ["Job Posting Template", "job_posting_template", "template"],
    ["Default Sequence Sender", "default_sequence_sender", ["Sequence Owner"]],
    ["Team Public Profile Sharing", "team_public_profile_sharing", "toggle"],
    ["Client Portal Default Job Access", "client_portal_default_job_access", ["Selected Jobs", "All Client Jobs"]],
  ],
];

const slaOptions = ["4 hours", "8 hours", "1 business day", "2 business days", "3 business days", "Off"];
const presentedOptions = ["1 business day", "2 business days", "3 business days", "5 business days", "7 business days", "Off"];
const securityDefaults = { session_timeout: "8h", require_mfa: false };

function EditableSettings({ category, title, initialValues, canManage = true, onSaved, children }) {
  const defaults = category === "workflows" ? workflowDefaults : category === "security" ? securityDefaults : notificationSettingsDefaults;
  const [draft, setDraft] = useState(defaults);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const dirtyRef = useRef(false);
  const savedValues = useMemo(() => ({ ...defaults, ...(initialValues || {}) }), [defaults, initialValues]);

  useEffect(() => { if (!dirtyRef.current) setDraft(savedValues); }, [savedValues]);
  const dirty = dirtyRef.current || JSON.stringify(draft) !== JSON.stringify(savedValues);
  const save = async () => {
    setBusy(true); setFeedback(null);
    try {
      const result = await settingsRequest(`settings/workspace/${category}`, { method: "PUT", body: draft });
      setDraft({ ...defaults, ...result.values });
      dirtyRef.current = false;
      onSaved?.(result.values);
      setFeedback({ severity: "success", text: `${title} settings saved.` });
    } catch (error) {
      setFeedback({ severity: "error", text: error.message });
    } finally {
      setBusy(false);
    }
  };

  return <SettingsCard title={title}>
    {!canManage && <Alert severity="info" sx={{ mb: 3 }}>Only an administrator can change organization defaults.</Alert>}
    {feedback && <Alert severity={feedback.severity} sx={{ mb: 3 }}>{feedback.text}</Alert>}
    {children(draft, (key, value) => { dirtyRef.current = true; setDraft(current => ({ ...current, [key]: value })); }, busy || !canManage)}
    {canManage && <SettingsActions dirty={dirty} busy={busy} onCancel={() => { dirtyRef.current = false; setDraft(savedValues); setFeedback(null); }} onSave={save} />}
  </SettingsCard>;
}

export default function SettingsPolicies({ kind, tab = 0, go, values, canManage, onSaved }) {
  if (kind === "security") return <EditableSettings category="security" title="Security & Privacy" initialValues={values?.security} canManage={canManage} onSaved={onSaved}>
    {(draft, change, disabled) => <>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Session timeout applies to new sign-ins. Existing administrator multi-factor authentication controls remain available to System administrators.</Typography>
      <FieldsGrid>
        <SettingsField label="Session Timeout" options={["1h", "4h", "8h", "12h", "24h", "7d"]} value={draft.session_timeout} disabled={disabled} onChange={value => change("session_timeout", value)} />
        <SettingsField label="Require MFA" options={[{ value: "disabled", label: "Off" }, { value: "enabled", label: "On" }]} value={draft.require_mfa ? "enabled" : "disabled"} disabled={disabled} onChange={value => change("require_mfa", value === "enabled")} />
      </FieldsGrid>
      <SettingsTable headers={["Protected Data", "Access"]} rows={[
        ["Sensitive Candidate Data", "Record scope and explicit permission"],
        ["Sensitive Commercial Data", "Explicit permission"],
        ["Financial / Revenue Data", "Explicit permission"],
        ["Internal Notes", "Record scope and explicit permission"],
      ]} />
    </>}
  </EditableSettings>;

  if (kind === "notifications") return <EditableSettings category="notifications" title="Organization Notification Defaults" initialValues={values?.notifications} canManage={canManage} onSaved={onSaved}>
    {(draft, change, disabled) => <>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Choose the default channels for organization notifications. Mandatory security and account alerts cannot be disabled.</Typography>
      <SettingsTable headers={["Notification Type", "In-App", "Email", "Locked"]} rows={notificationTypes.map(([key, label, mandatory]) => {
        const preference = { ...notificationDefaults[key], ...(draft.preferences?.[key] || {}) };
        return [label,
          <SettingsToggle label={`${label} in-app`} value={preference.in_app} disabled={disabled || mandatory} onChange={value => change("preferences", { ...draft.preferences, [key]: { ...preference, in_app: value } })} />,
          <SettingsToggle label={`${label} email`} value={preference.email} disabled={disabled || mandatory} onChange={value => change("preferences", { ...draft.preferences, [key]: { ...preference, email: value } })} />,
          mandatory ? "Yes" : "No"];
      })} />
    </>}
  </EditableSettings>;

  return <>
    <EditableSettings category="workflows" title={["Recruiting Workflow", "Sales / CRM Workflow", "Content & Sharing"][tab]} initialValues={values?.workflows} canManage={canManage} onSaved={onSaved}>
      {(draft, change, disabled) => <FieldsGrid>{workflowGroups[tab].map(([label, key, control]) => {
        if (control === "toggle") return <SettingsField key={key} id={`workflow-${key}`} label={label} value={draft[key] ? "true" : "false"} options={[{ value: "true", label: "On" }, { value: "false", label: "Off" }]} disabled={disabled} onChange={value => change(key, value === "true")} />;
        if (control === "sla" || control === "presented") return <SettingsField key={key} id={`workflow-${key}`} label={label} value={draft[key]} options={control === "presented" ? presentedOptions : slaOptions} disabled={disabled} onChange={value => change(key, value)} />;
        if (control === "template") return <SettingsField key={key} id={`workflow-${key}`} label={label} value={draft[key]} options={[draft[key] || "No active template", "No template"]} disabled={disabled} onChange={value => change(key, value)} />;
        return <SettingsField key={key} id={`workflow-${key}`} label={label} value={draft[key]} options={control} disabled={disabled} onChange={value => change(key, value)} />;
      })}{tab === 2 && <SettingsField id="organization-email-signature" label="Organization Email Signature" value={draft.organization_email_signature} multiline disabled={disabled} onChange={value => change("organization_email_signature", value)} />}</FieldsGrid>}
    </EditableSettings>
    {tab === 1 && <SettingsCard title="Client Portal Onboarding" description="Portal invitations are available to Admins and Team Members for Client companies.">
      <SettingsTable rows={[["Eligible Company Status", "Client"], ["Who Can Invite", "Admin and Team Member"], ["Default Job Access", values?.workflows?.client_portal_default_job_access || workflowDefaults.client_portal_default_job_access]]} />
      <Button sx={{ mt: 2 }} onClick={() => go("users", 1)}>Manage Client Portal Users</Button>
    </SettingsCard>}
    {tab === 2 && <Stack spacing={2}>
      <Alert severity="info">Public Job Posting Links can be disabled here. Existing published links must be removed from their job records.</Alert>
      <SettingsCard title="Email Signature" description="Personal signatures take precedence over the organization default.">
        <Typography color="text.secondary">Signature precedence: Personal Signature → Organization Default → None.</Typography>
      </SettingsCard>
    </Stack>}
  </>;
}
